import { forwardRef } from 'react';
import { Icon } from '../ui';
import { testId } from '../app/testId';
import { SEARCH_COPY } from './copy';
import { isMacPlatform } from './searchContext';
import styles from './Search.module.css';

// THE WAY IN, in the top bar (DESIGN_SYSTEM §10: "search / command palette field with the Ctrl/⌘ K hint").
//
// It is a BUTTON, not a field. A field in the bar would be a second place to type a name — the list already
// has one (`list.search`, T-041) — and the two would narrow different things a thumb's width apart. This one
// says what the keyboard already does and opens the one surface that searches everything.
//
// What gives way, in §10's order (search is the first thing to shrink): the `/` hint and the word "Search"
// both go at 980 px and the button becomes its icon. §10 writes that boundary at 1420 for a search FIELD;
// a 108 px button does not need to give way three columns early, and nothing else in the bar moves as a
// result (T-044's note).

export interface SearchTriggerProps {
  open: boolean;
  onOpen: () => void;
  /** The dialog's id, so the trigger says what it controls (T-035's layer contract). */
  paletteId: string;
}

export const SearchTrigger = forwardRef<HTMLButtonElement, SearchTriggerProps>(
  function SearchTrigger({ open, onOpen, paletteId }, ref) {
    const hint = isMacPlatform() ? SEARCH_COPY.triggerHintMac : SEARCH_COPY.triggerHint;
    return (
      <button
        ref={ref}
        type="button"
        className={styles.trigger}
        aria-label={SEARCH_COPY.triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? paletteId : undefined}
        title={hint}
        data-testid={testId('shell', 'search')}
        onClick={onOpen}
      >
        <Icon name="search" size={16} />
        <span className={styles.triggerLabel}>{SEARCH_COPY.trigger}</span>
        {/* Decorative: the shortcut is already in the button's `title` and in the palette's hint row, and a
            screen reader reading "slash" between "Search" and nothing would be noise. */}
        <kbd className={styles.triggerKey} aria-hidden="true">
          /
        </kbd>
      </button>
    );
  },
);
